import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/login` — the sign-in page. Sits outside the auth-gated `_app`
 * layout. "Continue with GitLab" is a plain link to the Hono
 * `GET /api/auth/signin` route, which kicks off the GitLab OAuth
 * handshake (port of the next-auth `signIn("gitlab")` flow).
 *
 * The local GitLab container is opt-in (`yarn start:gitlab`), so
 * plain `yarn start` has nothing for GitLab OAuth to talk to. The
 * loader probes `/api/auth/dev-users` — 404 outside dev, or when
 * `yarn start:gitlab` IS running and someone still wants the real
 * OAuth flow to test it — and only then does the page offer the
 * dev-only "sign in as" shortcut below the GitLab button.
 *
 * Clients without a GitLab account ask for a one-time link by e-mail.
 */
type DevUser = { id: string; name: string | null; email: string };

export const Route = createFileRoute("/login")({
  loader: async (): Promise<{ devUsers: DevUser[] | null }> => {
    try {
      const { users } = await api.get<{ users: DevUser[] }>("/api/auth/dev-users");
      return { devUsers: users };
    } catch {
      return { devUsers: null };
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const { devUsers } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-sidebar text-sidebar-foreground p-12">
        <div className="max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <img src="/netlion-mark.png" alt="Netlion" className="h-10 w-10 object-contain" />
            <span className="font-heading text-2xl font-semibold tracking-tight text-primary mt-3">
              HUB
            </span>
          </div>
          <h1 className="font-heading text-4xl font-bold leading-tight">
            All your tools. One central hub.
          </h1>
          <p className="text-sidebar-foreground/70 text-lg leading-relaxed">
            Every service your team relies on, connected and in sync — projects, time, billing, and
            beyond.
          </p>
        </div>
      </div>

      {/* Right panel — login */}
      <div className="flex flex-1 items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden flex items-center gap-3 justify-center">
            <img src="/netlion-mark.png" alt="Netlion" className="h-10 w-10 object-contain" />
            <span className="font-heading text-2xl font-semibold tracking-tight text-primary mt-3">
              HUB
            </span>
          </div>

          <div className="space-y-2 text-center lg:text-left">
            <h2 className="font-heading text-2xl font-bold">Welcome back</h2>
            <p className="text-muted-foreground text-sm">Sign in with your GitLab account</p>
          </div>

          <Button asChild className="w-full h-11 text-sm font-medium" data-testid="sign-in-gitlab">
            <a href="/api/auth/signin">
              <svg
                className="mr-2 h-5 w-5"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <title>GitLab</title>
                <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z" />
              </svg>
              Continue with GitLab
            </a>
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            Access is restricted to organization members
          </p>

          <EmailLinkForm />

          {devUsers && devUsers.length > 0 && <DevLoginForm users={devUsers} />}
        </div>
      </div>
    </div>
  );
}

function DevLoginForm({ users }: { users: DevUser[] }) {
  const [userId, setUserId] = useState(users[0]?.id ?? "");
  const login = useApiMutation(() => api.post("/api/auth/dev-login", { userId }), {
    // Full reload, same as the OAuth callback's redirect — picks up the
    // freshly-minted session cookie cleanly rather than racing the SPA's
    // own auth state.
    onSuccess: () => {
      window.location.href = "/";
    },
  });

  return (
    <div className="space-y-2 rounded-md border border-dashed p-3">
      <p className="text-xs font-medium text-muted-foreground">
        Dev login — no GitLab running (yarn start:gitlab for the real OAuth flow)
      </p>
      <div className="flex gap-2">
        <Select
          className="flex-1"
          value={userId}
          onChange={setUserId}
          options={users.map((u) => ({ value: u.id, label: u.name ?? u.email }))}
        />
        <Button
          type="button"
          variant="outline"
          disabled={login.pending || !userId}
          onClick={() => login.mutate()}
        >
          Sign in
        </Button>
      </div>
      {login.error && <p className="text-xs text-destructive">{login.error}</p>}
    </div>
  );
}

function EmailLinkForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const signIn = useApiMutation(() => api.post("/api/auth/password", { email, password }), {
    onSuccess: () => {
      window.location.href = "/";
    },
  });
  const request = useApiMutation(() => api.post("/api/auth/email-link", { email }), {
    onSuccess: () => setSent(true),
  });
  const inputClass =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  if (sent) {
    return (
      <p className="rounded-md border p-3 text-sm" data-testid="email-link-sent">
        If {email} has a client account, a sign-in link is on its way. It works once, for 15
        minutes.
      </p>
    );
  }
  return (
    <form
      className="space-y-2 border-t pt-6"
      onSubmit={(e) => {
        e.preventDefault();
        signIn.mutate();
      }}
    >
      <p className="text-sm font-medium">Client without GitLab?</p>
      <input
        id="client-email"
        type="email"
        required
        autoComplete="username"
        aria-label="E-mail"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className={inputClass}
      />
      <input
        id="client-password"
        type="password"
        autoComplete="current-password"
        aria-label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        className={inputClass}
      />
      <div className="flex gap-2">
        <Button
          type="submit"
          variant="outline"
          className="flex-1"
          disabled={signIn.pending || !email || !password}
        >
          Sign in
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="flex-1"
          disabled={request.pending || !email}
          onClick={() => request.mutate()}
        >
          E-mail me a link
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        No password yet, or forgot it? Get a sign-in link and set one in your profile.
      </p>
      {(signIn.error || request.error) && (
        <p className="text-xs text-destructive">{signIn.error ?? request.error}</p>
      )}
    </form>
  );
}
